import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, DownCaretIcon, PencilIcon, PlusIconPlain, Spinner, TextInput, WarningIcon, } from '@streamr/tv-ui';
import { executeSoon } from '@streamr/tv-utils';
import { hexToBinary } from '@streamr/utils';
import { addDays } from 'date-fns';
import pick from 'lodash/pick';
import { useCallback, useEffect, useId, useMemo, useRef, useState, } from 'react';
import styled from 'styled-components';
import { Env } from '../../Env';
import { generateKeysAndUserId } from '../../engine/helpers/generateKeysAndUserId';
import { generateBroadcastId, validateScheduledBroadcast, } from '../../services/schedule/ScheduledBroadcast';
import { ScheduleService } from '../../services/schedule/ScheduleService';
import { Popup } from '../../utils/popups';
import { generateStreamId } from '../../utils/streamId';
import { getThumbnail, uploadThumbnail } from '../../utils/thumbnail';
import { formatDate } from '../../utils/time';
import { BroadcastNameplate } from '../BroadcastNameplate';
import Label from '../Label';
import { LoadingIndicator } from '../LoadingIndicator';
import { PopupButton } from '../PopupButton';
import { DismissalReason } from './AbstractPopup';
import { showCalendar } from './CalendarPopup';
import { FormPopup } from './FormPopup';
import { showToast } from './ToastPopup';
export function BroadcastInfoEditPopup({ broadcast, title = 'Schedule broadcast', reject: rejectProp, resolve, onThumbnailChange, }) {
    const baseBroadcast = useMemo(() => {
        if (broadcast !== undefined) {
            return pick(broadcast, 'id', 'streamId', 'broadcasterPrivateKey', 'broadcasterUserId', 'aesKey');
        }
        const streamId = generateStreamId();
        const { privateKey, userId, aesKey } = generateKeysAndUserId();
        return {
            id: generateBroadcastId(),
            streamId,
            broadcasterPrivateKey: privateKey,
            broadcasterUserId: userId,
            aesKey,
        };
    }, [broadcast]);
    const [name, setName] = useState(broadcast?.name || '');
    const [savedThumbnail, setSavedThumbnail] = useState();
    const [isLoadingSavedThumbnail, setIsLoadingSavedThumbnail] = useState(false);
    const [thumbnail, setThumbnail] = useState();
    const [isSavingThumbnail, setIsSavingThumbnail] = useState(false);
    const { thumbnailError, isValidatingThumbnail, isValidatingThumbnailRef } = useThumbnailValidation(thumbnail);
    const thumbnailPreview = useMemo(() => {
        if (thumbnail)
            return URL.createObjectURL(thumbnail);
        if (savedThumbnail)
            return URL.createObjectURL(savedThumbnail);
        return null;
    }, [thumbnail, savedThumbnail]);
    useEffect(() => {
        return () => {
            if (thumbnailPreview) {
                URL.revokeObjectURL(thumbnailPreview);
            }
        };
    }, [thumbnailPreview]);
    const startsAt = broadcast?.startsAt;
    const [nextStartsAt, setNextStartsAt] = useState(startsAt);
    const nextLocalDate = useMemo(() => new Date(nextStartsAt || 0), [nextStartsAt]);
    const [time, setTime] = useState('');
    const localDateTimeString = `${formatDate(nextLocalDate, 'yyyy-MM-dd')}T${time}`;
    const [isDirty, setIsDirty] = useState(false);
    useEffect(() => {
        const defaultDate = addDays(Date.now(), 1);
        setNextStartsAt(startsAt ?? defaultDate.getTime());
        setTime(formatDate(startsAt || defaultDate, 'HH:mm'));
    }, [startsAt]);
    const validationErrors = useMemo(() => {
        return validateScheduledBroadcast({
            name,
            startsAt: new Date(localDateTimeString).getTime(),
            ...baseBroadcast,
        });
    }, [name, localDateTimeString, baseBroadcast]);
    const isValid = validationErrors.length === 0;
    const nameError = useMemo(() => {
        if (!isDirty)
            return null;
        const nameErrors = validationErrors.filter((issue) => issue.path[0] === 'name');
        if (nameErrors.length === 0)
            return null;
        // Get the first error for the name field
        const error = nameErrors[0];
        switch (error.code) {
            case 'invalid_type':
                return `Invalid input type: expected ${error.expected}`;
            case 'too_small':
                return `The title can not be shorter than ${error.minimum} characters.`;
            case 'too_big':
                return `The title can not be longer than ${error.maximum} characters.`;
            default:
                return 'The title is required';
        }
    }, [validationErrors, isDirty]);
    const dateError = useMemo(() => {
        if (!isDirty)
            return null;
        const dateErrors = validationErrors.filter((issue) => issue.path[0] === 'startsAt');
        if (dateErrors.length === 0)
            return null;
        // Get the first error for the name field
        const error = dateErrors[0];
        switch (error.code) {
            case 'invalid_type':
                return `Invalid input type: expected ${error.expected}`;
            case 'too_small':
                return 'The date can not be in the past.';
            default:
                return 'The date is required';
        }
    }, [validationErrors, isDirty]);
    const scheduleService = ScheduleService.getNotificationInterface();
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const reject = useCallback((reason) => {
        if (!isCalendarOpen) {
            rejectProp(reason);
        }
    }, [rejectProp, isCalendarOpen]);
    const dateControlRef = useRef(null);
    const calendarPopupControllerRef = useRef(null);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `validationErrors` is just the trigger.
    useEffect(() => {
        calendarPopupControllerRef.current?.refreshAnchors();
    }, [validationErrors]);
    useEffect(() => {
        if (!Env.isFeatureEnabled('ThumbnailUpload')) {
            return;
        }
        let cancelled = false;
        setIsLoadingSavedThumbnail(true);
        getThumbnail(baseBroadcast.broadcasterUserId, baseBroadcast.streamId)
            .then((file) => {
            if (cancelled)
                return;
            if (file) {
                setSavedThumbnail(file);
            }
        })
            .catch((_) => {
            // ignore the error - there might not be a thumbnail yet
        })
            .finally(() => {
            if (cancelled)
                return;
            setIsLoadingSavedThumbnail(false);
        });
        return () => {
            cancelled = true;
        };
    }, [baseBroadcast]);
    const nameInputId = useId();
    const timeInputId = useId();
    const imageUploadButtonId = useId();
    const fileInputRef = useRef(null);
    return (_jsx(FormPopup, { reject: reject, title: title, canSubmit: isValid &&
            !isSavingThumbnail &&
            !isValidatingThumbnail &&
            !thumbnailError, onSubmit: async () => {
            setIsDirty(true);
            if (!isValid ||
                isValidatingThumbnailRef.current ||
                Boolean(thumbnailError)) {
                return;
            }
            if (thumbnail) {
                setIsSavingThumbnail(true);
                try {
                    await uploadThumbnail(thumbnail, baseBroadcast.streamId, hexToBinary(baseBroadcast.broadcasterPrivateKey), baseBroadcast.broadcasterUserId);
                    onThumbnailChange?.(thumbnail);
                }
                catch (error) {
                    console.error('Failed to upload the thumbnail.', error);
                    showToast('Failed to upload the thumbnail.');
                }
                finally {
                    setIsSavingThumbnail(false);
                }
            }
            const modifiedBroadcast = {
                name,
                startsAt: new Date(localDateTimeString).getTime(),
                ...baseBroadcast,
            };
            if (broadcast) {
                scheduleService.notifyUpdateScheduledBroadcast(modifiedBroadcast);
            }
            else {
                scheduleService.notifyAddScheduledBroadcast(modifiedBroadcast);
            }
            resolve();
        }, cancelButtonProps: {
            onClick: () => {
                reject(DismissalReason.CloseButton);
            },
        }, okButtonProps: {
            label: broadcast?.id ? 'Update' : 'Create',
        }, children: _jsxs(Body, { children: [_jsx(LoadingIndicator, { loading: isSavingThumbnail }), _jsx(BroadcastNameplate, { broadcast: {
                        name,
                        startsAt: startsAt ?? Date.now(),
                        ...baseBroadcast,
                    } }), _jsxs("div", { children: [_jsx(Label, { htmlFor: nameInputId, children: "Event title" }), _jsx(TextInput, { id: nameInputId, placeholder: "Untitled event", value: name, onChange: (e) => {
                                setIsDirty(true);
                                setName(e.target.value);
                            }, invalid: isDirty && !!nameError }), isDirty && nameError && (_jsxs(ErrorText, { children: [_jsx(WarningIcon, {}), nameError] }))] }), Env.isFeatureEnabled('ThumbnailUpload') && (_jsxs("div", { children: [_jsx(Label, { htmlFor: imageUploadButtonId, children: "Event thumbnail" }), !thumbnailPreview && (_jsx(ImageUploadButton, { type: "button", id: imageUploadButtonId, onClick: () => {
                                fileInputRef.current?.click();
                            }, disabled: isLoadingSavedThumbnail, children: isLoadingSavedThumbnail ? (_jsx(Spinner, {})) : (_jsx(StyledPlusIconPlain, { size: "x1_5" })) })), thumbnailPreview && (_jsxs(ImageUploadButton, { as: "div", children: [_jsx(ThumbnailPreview, { src: thumbnailPreview, alt: "Thumbnail" }), _jsx(ImageChangeButton, { type: "button", id: imageUploadButtonId, onClick: () => {
                                        fileInputRef.current?.click();
                                    }, children: _jsx(PencilIcon, { size: "x1" }) })] })), thumbnailError && (_jsxs(ErrorText, { children: [_jsx(WarningIcon, {}), thumbnailError] })), _jsx("input", { hidden: true, type: "file", ref: fileInputRef, accept: "image/jpeg", onChange: (e) => {
                                const file = e.target
                                    .files?.[0];
                                if (file) {
                                    setThumbnail(file);
                                }
                            } })] })), _jsxs("div", { children: [_jsx(Label, { htmlFor: "date", children: "Schedule" }), _jsxs(DateTimeControls, { children: [_jsx(Control, { ref: dateControlRef, children: _jsx(DateTimeButton, { type: "button", onClick: () => {
                                            showCalendar(nextLocalDate, {
                                                controllerRef: calendarPopupControllerRef,
                                                anchorRef: dateControlRef,
                                                onDateChange: (newDate) => {
                                                    setNextStartsAt(newDate.getTime());
                                                    setIsDirty(true);
                                                },
                                                onBeforeOpen: () => {
                                                    setIsCalendarOpen(true);
                                                },
                                                onAfterClose: () => {
                                                    setIsCalendarOpen(false);
                                                },
                                            });
                                        }, appendComponent: _jsx(DownCaretIcon, {}), children: formatDate(nextLocalDate, 'LLL d, yyyy') }) }), _jsx(Control, { children: _jsx(TextInput, { id: timeInputId, type: "time", value: time, onChange: (e) => {
                                            setIsDirty(true);
                                            setTime(e.target.value);
                                        } }) })] }), isDirty && dateError && (_jsxs(ErrorText, { children: [_jsx(WarningIcon, {}), dateError] }))] })] }) }));
}
const DateTimeButton = styled(PopupButton) `
    min-height: 0;
    height: 3.375rem;
    background-color: var(--tv-ui-color-1);
    box-shadow: 1px 1.5px 4px 0px #0000001A inset,
        1px 1.5px 4px 0px #00000014 inset,
        0px -0.5px 1px 0px #FFFFFF40 inset,
        0px -0.5px 1px 0px #FFFFFF4D inset;

    &:hover {
        background-color: var(--tv-ui-color-1);
        transition-duration: 50ms;
    }

    &:focus {
        box-shadow: 0px 1px 1px rgba(255, 255, 255, 0.3), inset 0px 2px 6px rgba(39, 39, 39, 0.4), 0 0 0 2px rgba(239, 239, 239, 0.3);
    }
`;
const Control = styled.div ``;
const DateTimeControls = styled.div `
    display: grid;
    gap: 0.375rem;

    @media (min-width: 25rem) {
        grid-template-columns: 1fr 1fr;
    }
`;
const Body = styled.div `
    display: grid;
    gap: 2rem;
    padding: 1.5rem;
    padding-top: 0;
`;
const ErrorText = styled.div `
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 0.25rem;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 0.875rem;
    color: #ffffff;
    margin-top: 0.25rem;
`;
const ImageUploadButton = styled(Button) `
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14rem;
    aspect-ratio: 16/9;
    --Button_Background: var(--tv-ui-color-14);
    --Button_HoverBackground: var(--tv-ui-color-14);
    --Button_ActiveBackground: var(--tv-ui-color-14);
    --Button_HoverTransform: scale(1.02);
    --Button_ActiveTransform: scale(1.02);
    position: relative;
    border-radius: 0.75rem;
`;
const StyledPlusIconPlain = styled(PlusIconPlain) `
    svg {
        width: 1.35rem;
        height: 1.35rem;
    }
`;
const ThumbnailPreview = styled.img `
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0.75rem;
`;
const ImageChangeButton = styled(Button) `
    position: absolute;
    bottom: 0.5rem;
    right: 0.5rem;
    width: 2.5rem;
    height: 2.5rem;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    --Button_HoverTransform: none;
    --Button_ActiveTransform: none;
`;
const broadcastInfoEditPopup = Popup.create(BroadcastInfoEditPopup);
export function showBroadcastInfoEditPopup(options = {}) {
    executeSoon(async () => {
        options.onOpenPopup?.();
        try {
            await broadcastInfoEditPopup.pop({
                broadcast: options.broadcast,
                title: options.title,
                onThumbnailChange: options.onThumbnailChange,
            });
        }
        catch (_ignore) {
        }
        finally {
            options.onClosePopup?.();
        }
    });
}
function useThumbnailValidation(thumbnail) {
    const [thumbnailError, setThumbnailError] = useState();
    const [isValidatingThumbnail, setIsValidatingThumbnail] = useState(false);
    const thumbnailValidationIdRef = useRef(0);
    const isValidatingThumbnailRef = useRef(false);
    useEffect(() => {
        let cancelled = false;
        let img = null;
        let objectUrl = null;
        const validationId = ++thumbnailValidationIdRef.current;
        const cleanup = () => {
            cancelled = true;
            if (img) {
                img.onload = null;
                img.onerror = null;
                img.src = '';
                img.remove();
                img = null;
            }
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
                objectUrl = null;
            }
        };
        if (!thumbnail) {
            setThumbnailError(undefined);
            isValidatingThumbnailRef.current = false;
            setIsValidatingThumbnail(false);
            return cleanup;
        }
        if (thumbnail.size > 10 * 1024 * 1024) {
            setThumbnailError('The thumbnail must be less than 10MB');
            isValidatingThumbnailRef.current = false;
            setIsValidatingThumbnail(false);
            return cleanup;
        }
        if (thumbnail.type !== 'image/jpeg') {
            setThumbnailError('The thumbnail must be a JPG image');
            isValidatingThumbnailRef.current = false;
            setIsValidatingThumbnail(false);
            return cleanup;
        }
        isValidatingThumbnailRef.current = true;
        setIsValidatingThumbnail(true);
        img = new window.Image();
        objectUrl = URL.createObjectURL(thumbnail);
        const finalize = () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
                objectUrl = null;
            }
            if (img) {
                img.onload = null;
                img.onerror = null;
                img.remove();
                img = null;
            }
            if (validationId === thumbnailValidationIdRef.current) {
                isValidatingThumbnailRef.current = false;
                setIsValidatingThumbnail(false);
            }
        };
        img.onload = () => {
            if (cancelled)
                return;
            if (img && (img.width > 1920 || img.height > 1080)) {
                setThumbnailError('The thumbnail must not be larger than 1920x1080 pixels');
            }
            else {
                setThumbnailError(undefined);
            }
            finalize();
        };
        img.onerror = () => {
            if (cancelled)
                return;
            setThumbnailError('Could not analyze image');
            finalize();
        };
        img.src = objectUrl;
        return cleanup;
    }, [thumbnail]);
    return {
        thumbnailError,
        isValidatingThumbnail,
        isValidatingThumbnailRef,
    };
}
